import { media, font, color } from "@universityofmaryland/web-token-library";
import { objectWithClassName } from "../utilities/create/jss.js";
import "../node_modules/.pnpm/postcss@8.5.8/node_modules/postcss/lib/postcss.js";
import "../node_modules/.pnpm/postcss-js@4.1.0_postcss@8.5.8/node_modules/postcss-js/index.js";
import "../node_modules/.pnpm/postcss-nesting@13.0.2_postcss@8.5.8/node_modules/postcss-nesting/dist/index.js";
import "../_virtual/index.js";
const breakpointMedium = media.queries.medium.min;
const breakpointLarge = media.queries.large.min;
const breakpointDesktop = media.queries.desktop.min;
const scalingContainerSmall = media.breakpointValues.small.min;
const scalingContainerMedium = media.breakpointValues.medium.min;
const scalingContainerLarge = media.breakpointValues.large.min;
const sizeExtraLarge = {
  fontSize: font.size["4xl"],
  lineHeight: `1.125em`
};
const sizeExtraLargeResponsive = {
  fontSize: `calc(${font.size["lg"]} + 1.16vw)`
};
const sizeLarger = {
  fontSize: font.size["2xl"],
  lineHeight: `1.25em`
};
const sizeLargerResponsive = {
  fontSize: `calc(${font.size["lg"]} + 0.5vw)`
};
const sizeLarge = {
  fontSize: font.size["lg"],
  lineHeight: `1.25em`
};
const sizeSmall = {
  fontSize: font.size["base"],
  lineHeight: `1.375em`
};
const sizeSmaller = {
  fontSize: font.size["sm"],
  lineHeight: `1.28em`
};
const sizeMin = {
  fontSize: font.size["min"],
  lineHeight: `1.16em`
};
const largestBase = {
  fontFamily: font.family["sans"],
  fontWeight: font.weight["bold"],
  textWrap: "pretty",
  ...sizeLarger,
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["3xl"]} + 2vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    fontSize: font.size["5xl"],
    lineHeight: `1.04em`
  }
};
const extraLargeBase = {
  fontFamily: font.family["sans"],
  textWrap: "pretty",
  ...sizeLarger,
  [`@media (${breakpointLarge})`]: {
    ...sizeExtraLargeResponsive
  },
  [`@media (${breakpointDesktop})`]: {
    ...sizeExtraLarge
  }
};
const largerBase = {
  fontFamily: font.family["sans"],
  ...sizeLarge,
  textWrap: "pretty",
  lineHeight: `1.40em`,
  [`@media (${breakpointLarge})`]: {
    ...sizeLargerResponsive
  },
  [`@media (${breakpointDesktop})`]: {
    ...sizeLarger
  }
};
const scalingLargerBase = {
  fontFamily: font.family["sans"],
  fontSize: font.size["lg"],
  lineHeight: `1.25em`,
  textWrap: "pretty",
  [`@container (min-width: ${scalingContainerSmall}px)`]: {
    ...sizeLargerResponsive
  },
  [`@container (min-width: ${scalingContainerLarge}px)`]: {
    fontSize: font.size["4xl"]
  }
};
const largeBase = {
  fontFamily: font.family["sans"],
  fontWeight: font.weight["bold"],
  ...sizeLarge
};
const mediumBase = {
  fontFamily: font.family["sans"],
  ...sizeSmall,
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["base"]} + 0.16vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    fontSize: font.size["lg"],
    lineHeight: `1.55em`
  }
};
const smallBase = {
  fontFamily: font.family["sans"],
  fontSize: font.size["sm"],
  lineHeight: `1.375em`,
  [`@media (${breakpointMedium})`]: {
    ...sizeSmall
  },
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["sm"]} + 0.16vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    ...sizeSmall
  }
};
const smallerBase = {
  fontFamily: font.family["sans"],
  ...sizeSmaller,
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["min"]} + 0.16vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    ...sizeSmaller
  }
};
const minBase = {
  fontFamily: font.family["sans"],
  ...sizeMin
};
const scalingMinBase = {
  ...minBase,
  [`@container (min-width: ${scalingContainerMedium}px)`]: {
    ...smallBase
  }
};
function compose(size, options) {
  const { theme, color: explicitColor, scaling = false } = options || {};
  let base;
  if (scaling && (size === "larger" || size === "min")) {
    base = size === "larger" ? scalingLargerBase : scalingMinBase;
  } else {
    const sizes = {
      largest: largestBase,
      extralarge: extraLargeBase,
      larger: largerBase,
      large: largeBase,
      medium: mediumBase,
      small: smallBase,
      smaller: smallerBase,
      min: minBase
    };
    base = sizes[size];
  }
  const finalColor = explicitColor || (theme === "dark" ? color.white : color.black);
  const composed = {
    ...base,
    ...theme && {
      color: finalColor
    }
  };
  const classNameParts = ["umd-sans", size];
  if (scaling) classNameParts.push("scaling");
  if (theme === "dark") classNameParts.push("dark");
  return objectWithClassName({
    ...composed,
    className: classNameParts.join("-")
  });
}
const largest = compose("largest");
const extraLarge = compose("extralarge");
const larger = compose("larger");
const large = compose("large");
const medium = compose("medium");
const small = compose("small");
const smaller = compose("smaller");
const min = compose("min");
const scalingLarger = compose("larger", { scaling: true });
const scalingMin = compose("min", { scaling: true });
const fonts = {
  largest: compose("largest"),
  extraLarge: compose("extralarge"),
  larger: compose("larger"),
  large: compose("large"),
  medium: compose("medium"),
  small: compose("small"),
  smaller: compose("smaller"),
  min: compose("min")
};
const scalingFonts = {
  larger: compose("larger", { scaling: true }),
  min: compose("min", { scaling: true })
};
const transformations = (() => {
  const { className: _, ...largestStyles } = largest;
  const { className: __, ...extraLargeStyles } = extraLarge;
  const { className: ___, ...largerStyles } = larger;
  return {
    largestUppercase: objectWithClassName({
      className: "umd-sans-largest-uppercase",
      ...largestStyles,
      fontWeight: font.weight["extraBold"],
      textTransform: "uppercase"
    }),
    extraLargeUppercase: objectWithClassName({
      className: "umd-sans-extralarge-uppercase",
      ...extraLargeStyles,
      fontWeight: font.weight["extraBold"],
      textTransform: "uppercase"
    }),
    extraLargeBold: objectWithClassName({
      className: "umd-sans-extralarge-bold",
      ...extraLargeStyles,
      fontWeight: font.weight["bold"]
    }),
    largerBold: objectWithClassName({
      className: "umd-sans-larger-bold",
      ...largerStyles,
      fontWeight: font.weight["bold"]
    })
  };
})();
export {
  compose,
  extraLarge,
  fonts,
  large,
  larger,
  largest,
  medium,
  min,
  scalingFonts,
  scalingLarger,
  scalingMin,
  small,
  smaller,
  transformations
};
//# sourceMappingURL=sans.js.map
